// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { ErrorCause, Feedback, Tag, TagPayload } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface FormEditTagProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditTag = ({ onClose, onSuccess, setIsLoading }: FormEditTagProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [searchParams] = useSearchParams();
	const [tag, setTag] = useState<Tag | null>(null);
	const [values, setValues] = useState({
		name: '',
	});

	// params
	const paramTag = searchParams.get('dialogTag');

	// context
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-tag';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch tag
	const { isLoading: isLoadingTag } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/tags/${paramTag}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setTag(res.data[0]);
			}
		},
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (!paramTag) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const body: TagPayload = {
				name: String(entries.name),
			};

			const fetchEditTag = await fetch(`${process.env.REACT_APP_API_URL}/tags/${paramTag}`, {
				method: 'PATCH',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resEditTag = await fetchEditTag.json();

			if (resEditTag.error) {
				throw new Error(resEditTag.error.message, {
					cause: {
						id: resEditTag.error.id,
						details: resEditTag.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.tagUpdated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'TAG_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.tagNonExistent', { id: tag?.id });
				}

				if (cause.id === 'ID_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.idNonExistent');
				}

				if (cause.id === 'TAG_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	// content
	const renderContent = () => {
		if (isLoadingTag) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	// set default values when tag changes
	useEffect(() => {
		if (tag) {
			setValues({
				name: tag.name,
			});
		}
	}, [tag]);

	return (
		<form id={`${idForm}`} name="formEditTag" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
